/**
 * Header Component
 * Top fixed header with page title, search, notifications, and user menu
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getNavByRole } from './navConfig';
import { BellIcon, UserIcon, LogoutIcon, SearchIcon, CalendarIcon, SettingsIcon, CartIcon } from './icons';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotificationsStore } from '../store/notificationsStore';
import { useCartStore } from '../store/cartStore';
import { globalSearch, SearchResults } from '../services/searchApi';
import styles from './Header.module.css';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Live unread count for the bell badge. We poll the notifications list on
  // first mount and every 60s while mounted so the badge stays accurate
  // even if the user never opens the dropdown.
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  // Customer cart — live item count for the header cart badge. Reactive: any
  // addItem / setQuantity / removeItem call from anywhere in the app re-renders
  // this badge instantly because we subscribe to the items array.
  const cartItems = useCartStore((s) => s.items);
  const cartCount = cartItems.reduce((sum, l) => sum + l.quantity, 0);
  const role = (user?.role || '').toLowerCase();
  const isCustomer = role === 'customer';
  // Global search is enabled for admin/sales-rep/call-center; customers
  // would need a different scope (their own orders/catalog), so keep them
  // out of this implementation.
  const searchEnabled = role === 'admin' || role === 'sales rep' || role === 'call center';

  // ── Global search state ──
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  // Debounce the query → fire one request 250ms after the user stops typing.
  useEffect(() => {
    if (!searchEnabled) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    const t = window.setTimeout(() => {
      globalSearch(q)
        .then((res) => {
          setSearchResults(res);
          setSearchOpen(true);
        })
        .catch(() => setSearchResults(null))
        .finally(() => setSearching(false));
    }, 250);
    return () => window.clearTimeout(t);
  }, [searchQuery, searchEnabled]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!searchOpen) return;
    const onClick = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [searchOpen]);

  const goTo = (path: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(path);
  };
  const fetchNotifications = useNotificationsStore((s) => s.fetchList);
  useEffect(() => {
    if (!user) return;
    fetchNotifications({ limit: 25 });
    const id = setInterval(() => fetchNotifications({ limit: 25 }), 60_000);
    return () => clearInterval(id);
  }, [user, fetchNotifications]);

  // Get page title from current route
  const getPageTitle = () => {
    const navConfig = getNavByRole(user?.role || 'default');
    for (const section of navConfig) {
      for (const item of section.items) {
        if (item.path === location.pathname) {
          return item.label;
        }
      }
    }
    return 'Dashboard';
  };

  // Get current date in readable format
  const getCurrentDate = () => {
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className={styles.header}>
      {/* Left Section - Page Title and Date */}
      <div className={styles.leftSection}>
        <h1 className={styles.pageTitle}>{getPageTitle()}</h1>
        <div className={styles.pageDateRow}>
          <CalendarIcon />
          <span className={styles.pageDate}>{getCurrentDate()}</span>
        </div>
      </div>

      {/* Right Section - Search, Notifications and User Menu */}
      <div className={styles.rightSection}>
        {/* Search Bar */}
        <div className={styles.searchBar} ref={searchWrapRef} style={{ position: 'relative' }}>
          <SearchIcon />
          <input
            type="text"
            placeholder={searchEnabled ? 'Search customers, products, orders…' : 'Search…'}
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => { if (searchResults) setSearchOpen(true); }}
            readOnly={!searchEnabled}
            disabled={!searchEnabled}
            title={searchEnabled ? 'Global search' : 'Search not available for your role'}
          />
          {searchEnabled && searchOpen && (searching || searchResults) && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
                maxHeight: 380,
                overflowY: 'auto',
                zIndex: 1100,
              }}
            >
              {searching && (
                <div style={{ padding: 12, fontSize: 12, color: '#6B7280' }}>Searching…</div>
              )}
              {!searching && searchResults && searchResults.count === 0 && (
                <div style={{ padding: 12, fontSize: 12, color: '#6B7280' }}>No matches</div>
              )}
              {!searching && searchResults && (searchResults.customers?.length ?? 0) > 0 && (
                <div style={{ padding: '6px 0' }}>
                  <div style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6B7280' }}>
                    Customers
                  </div>
                  {searchResults.customers!.map((c) => (
                    <button
                      key={`c-${c.id}`}
                      type="button"
                      onClick={() => goTo(`/customer-details?id=${c.id}`)}
                      style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
                    >
                      <strong style={{ color: '#111827' }}>{c.full_name}</strong>
                      <span style={{ color: '#6B7280', marginLeft: 8 }}>{c.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {!searching && searchResults && (searchResults.skus?.length ?? 0) > 0 && (
                <div style={{ padding: '6px 0', borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6B7280' }}>
                    Products
                  </div>
                  {searchResults.skus!.map((s) => (
                    <button
                      key={`s-${s.id}`}
                      type="button"
                      onClick={() => goTo(`/product-catalog`)}
                      style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
                    >
                      <strong style={{ color: '#111827' }}>{s.product_name}</strong>
                      <span style={{ color: '#6B7280', marginLeft: 8 }}>{s.sku_code}</span>
                    </button>
                  ))}
                </div>
              )}
              {!searching && searchResults && (searchResults.orders?.length ?? 0) > 0 && (
                <div style={{ padding: '6px 0', borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ padding: '4px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6B7280' }}>
                    Orders
                  </div>
                  {searchResults.orders!.map((o) => (
                    <button
                      key={`o-${o.id}`}
                      type="button"
                      onClick={() => goTo(`/orders/details?id=${o.id}`)}
                      style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
                    >
                      <strong style={{ color: '#111827' }}>ORD-{String(o.id).padStart(6, '0')}</strong>
                      <span style={{ color: '#6B7280', marginLeft: 8 }}>{o.customer_name} · ${Number(o.total_amount).toFixed(2)} · {o.status}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cart button + badge — only visible to customers */}
        {isCustomer && (
          <button
            className={styles.notificationButton}
            title="Cart"
            onClick={() => navigate('/customer-cart')}
            style={{ position: 'relative' }}
          >
            <CartIcon />
            {cartCount > 0 && (
              <span className={styles.notificationBadge}>
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </button>
        )}

        {/* Notification Bell + Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            className={styles.notificationButton}
            title="Notifications"
            onClick={() => setShowNotifications((v) => !v)}
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className={styles.notificationBadge}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <NotificationDropdown
            open={showNotifications}
            onClose={() => setShowNotifications(false)}
          />
        </div>

        {/* User Avatar and Menu */}
        <div className={styles.userMenuContainer} ref={userMenuRef}>
          <button
            className={styles.userButton}
            onClick={() => setShowUserMenu(!showUserMenu)}
            title={user?.full_name || 'User'}
          >
            <div className={styles.userAvatar}>
              {user?.full_name.charAt(0).toUpperCase()}
            </div>
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className={styles.dropdownMenu}>
              <div className={styles.menuHeader}>
                <div className={styles.userInfoRow}>
                  <div className={styles.userAvatar}>
                    {user?.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.userInfo}>
                    <div className={styles.menuUserName}>{user?.full_name}</div>
                    <div className={styles.menuUserRole}>{user?.role || 'User'}</div>
                  </div>
                </div>
              </div>
              <div className={styles.menuDivider} />
              <button
                className={styles.menuItem}
                onClick={() => navigate('/profile')}
                title="View and edit profile"
              >
                <UserIcon />
                <div className={styles.menuItemContent}>
                  <div className={styles.menuItemLabel}>My Profile</div>
                  <div className={styles.menuItemDescription}>View and edit profile</div>
                </div>
              </button>
              <button
                className={styles.menuItem}
                onClick={() => navigate('/settings')}
                title="Preferences and configuration"
              >
                <SettingsIcon />
                <div className={styles.menuItemContent}>
                  <div className={styles.menuItemLabel}>Settings</div>
                  <div className={styles.menuItemDescription}>Preferences and configuration</div>
                </div>
              </button>
              <div className={styles.menuDivider} />
              <button
                className={`${styles.menuItem} ${styles.menuItemLogout}`}
                onClick={handleLogout}
                title="Sign out of your account"
              >
                <LogoutIcon />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
